import { useEffect, useState } from 'react'
import {
  PiCheckCircle,
  PiCircleNotch,
  PiPencilSimple,
  PiWarningCircle,
} from 'react-icons/pi'

import { cn } from 'components/UI/cn'
import { formatRelativeTime } from 'utils/formatting'

export type SequenceSaveStatusValue =
  | 'idle'
  | 'unsaved'
  | 'saving'
  | 'saved'
  | 'error'

interface Props {
  /** Current save state from the wizard's autosave machine. */
  status: SequenceSaveStatusValue
  /** Most recent successful save, used to render "Saved · 5m ago". */
  lastSavedAt?: Date | null
  /** When true, render a muted "Read-only" pill and ignore status. */
  readOnly?: boolean
  className?: string
}

const PILL_BASE =
  'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xxs font-medium'

/** Color-coded autosave pill; re-ticks the relative timestamp every 30s. */
export function SequenceSaveStatus({
  status,
  lastSavedAt,
  readOnly,
  className,
}: Props) {
  // Tick the clock so the relative timestamp ages even when nothing else
  // re-renders the component.
  const [, setNow] = useState(0)
  useEffect(() => {
    if (readOnly || status !== 'saved' || !lastSavedAt) return
    const id = window.setInterval(() => setNow((n) => n + 1), 30_000)
    return () => window.clearInterval(id)
  }, [readOnly, status, lastSavedAt])

  if (readOnly) {
    return (
      <span
        className={cn(
          PILL_BASE,
          'gap-1 border border-line bg-surface-alt text-fg-secondary',
          className
        )}
      >
        Read-only
      </span>
    )
  }

  // A single, always-mounted live region: screen readers only announce
  // mutations to a region already in the DOM, so the inner content is
  // swapped per status rather than each state mounting its own node.
  return (
    <span aria-live="polite" aria-atomic="true">
      {status === 'unsaved' && (
        <span
          className={cn(
            PILL_BASE,
            'bg-warning-bg-subtle text-warning-strong',
            className
          )}
        >
          <PiPencilSimple className="size-3.5 text-warning-strong" />
          Unsaved changes
        </span>
      )}
      {status === 'saving' && (
        <span
          className={cn(
            PILL_BASE,
            'border border-line bg-surface-alt text-fg-secondary',
            className
          )}
        >
          <PiCircleNotch className="size-3.5 animate-spin" />
          Saving...
        </span>
      )}
      {status === 'error' && (
        <span
          className={cn(
            PILL_BASE,
            'bg-error-bg-subtle text-error-strong',
            className
          )}
        >
          <PiWarningCircle className="size-3.5 text-error-strong" />
          Couldn&rsquo;t save
        </span>
      )}
      {status === 'saved' && (
        <span
          className={cn(
            PILL_BASE,
            'bg-success-bg-subtle text-success-strong',
            className
          )}
        >
          <PiCheckCircle className="size-3.5 text-success-strong" />
          Saved
          {lastSavedAt && (
            <span className="text-fg-secondary">
              · {humanizeJustSaved(lastSavedAt)}
            </span>
          )}
        </span>
      )}
    </span>
  )
}

/** "just now" for the first ~10s after a save, then "1m ago", "5m ago", ... */
function humanizeJustSaved(at: Date): string {
  const diffSeconds = Math.floor((Date.now() - at.getTime()) / 1000)
  if (diffSeconds < 10) return 'just now'
  return formatRelativeTime(at.toISOString())
}
